import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import Sidebar from './Sidebar';
import WhatsAppGroups from './WhatsAppGroups';
import SendOffers from './SendOffers';
import WhatsAppHistory from './WhatsAppHistory';
import StatsDashboard from './StatsDashboard';
import '../Admin.css';

const API_URL = process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:5001';

const WhatsAppManager = () => {
    const [status, setStatus] = useState({ isReady: false, hasQR: false, qrCode: null });
    const [activeTab, setActiveTab] = useState('connection'); // connection, groups, send, history
    const [socket, setSocket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Conectar ao Socket.io
        const newSocket = io(API_URL);
        setSocket(newSocket);

        // Listeners de eventos do WhatsApp
        newSocket.on('qr', (qrCode) => {
            console.log('📱 QR Code recebido');
            setStatus(prev => ({ ...prev, hasQR: true, qrCode }));
        });

        newSocket.on('ready', () => {
            console.log('✅ WhatsApp conectado!');
            setStatus({ isReady: true, hasQR: false, qrCode: null });
            fetchStatus();
        });

        newSocket.on('authenticated', () => {
            console.log('✅ Autenticado');
            // Ao autenticar, já podemos tentar buscar o status
            fetchStatus();
        });

        newSocket.on('disconnected', () => {
            console.log('🔌 Desconectado');
            setStatus({ isReady: false, hasQR: false, qrCode: null });
        });

        // Buscar status inicial
        fetchStatus();

        // Polling de backup (caso o socket falhe durante a sincronização inicial)
        const interval = setInterval(() => {
            if (!status.isReady) {
                fetchStatus();
            }
        }, 5000);

        return () => {
            newSocket.close();
            clearInterval(interval);
        };
    }, [status.isReady]);

    const fetchStatus = async () => {
        try {
            setError(null);
            const response = await axios.get(`${API_URL}/api/whatsapp/status`);
            setStatus(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Erro ao buscar status:', error);
            setError('Não foi possível conectar ao servidor do WhatsApp. Verifique se o backend está rodando.');
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (window.confirm('Deseja realmente desconectar o WhatsApp?')) {
            try {
                await axios.post(`${API_URL}/api/whatsapp/disconnect`);
                setStatus({ isReady: false, hasQR: false, qrCode: null });
            } catch (error) {
                console.error('Erro ao desconectar:', error);
                alert('Erro ao desconectar WhatsApp');
            }
        }
    };

    const handleRestart = async () => {
        if (window.confirm('Deseja reiniciar a conexão e gerar um novo QR Code?')) {
            try {
                setLoading(true);
                await axios.post(`${API_URL}/api/whatsapp/restart`);
                // Status updated via socket events
            } catch (error) {
                console.error('Erro ao reiniciar:', error);
                alert('Erro ao reiniciar WhatsApp');
                setLoading(false);
            }
        }
    };

    const handleHardReset = async () => {
        if (window.confirm('⚠️ ATENÇÃO: Isso apagará todos os dados de sessão do WhatsApp e forçará um novo login.\n\nUse apenas se estiver com problemas persistentes de conexão.\n\nDeseja continuar?')) {
            try {
                setLoading(true);
                await axios.post(`${API_URL}/api/whatsapp/hard-reset`);
                alert('Reset iniciado! Aguarde alguns segundos para o novo QR Code.');
            } catch (error) {
                console.error('Erro ao resetar:', error);
                alert('Erro ao realizar reset total');
                setLoading(false);
            }
        }
    };



    if (loading) {
        return (
            <div className="admin-layout">
                <Sidebar />
                <div className="main-content">
                    <div className="loading-container" style={{ textAlign: 'center', marginTop: '50px' }}>
                        <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '2rem', color: 'var(--primary-color)' }}></i>
                        <p style={{ marginTop: '15px' }}>Carregando dados do WhatsApp...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-layout">
                <Sidebar />
                <div className="main-content">
                    <div className="error-card" style={{ padding: '20px', backgroundColor: '#fff5f5', borderLeft: '4px solid #ff3b3c', borderRadius: '4px' }}>
                        <h3 style={{ color: '#ff3b3c' }}><i className="fas fa-exclamation-triangle"></i> Erro de Conexão</h3>
                        <p>{error}</p>
                        <button className="btn-primary" onClick={fetchStatus} style={{ marginTop: '10px' }}>
                            Tentar Novamente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-layout">
            <Sidebar />

            <div className="main-content">
                <div className="page-header">
                    <h2><i className="fab fa-whatsapp"></i> Gerenciar WhatsApp</h2>
                </div>

                {/* Dashboard de Estatísticas */}
                <StatsDashboard />

                {/* Status Card */}
                <div className="card-container" style={{ marginBottom: '20px' }}>
                    <div className="card-header">
                        <h3>Status da Conexão</h3>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            {!status.isReady && (
                                <>
                                    <button className="btn-primary" onClick={handleRestart}>
                                        <i className="fas fa-sync-alt"></i> Atualizar QR Code
                                    </button>
                                    <button
                                        className="btn-secondary"
                                        onClick={handleHardReset}
                                        style={{ backgroundColor: '#ff9800', color: 'white' }}
                                        title="Use se não conseguir conectar mesmo após atualizar"
                                    >
                                        <i className="fas fa-bomb"></i> Reset Total
                                    </button>
                                </>
                            )}
                            {status.isReady && (
                                <button className="btn-secondary" onClick={handleDisconnect} style={{ backgroundColor: '#dc3545' }}>
                                    <i className="fas fa-sign-out-alt"></i> Desconectar
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{ padding: '20px' }}>
                        {status.isReady ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: '#00a650',
                                    animation: 'pulse 2s infinite'
                                }}></div>
                                <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#00a650' }}>
                                    ✅ WhatsApp Conectado
                                </span>
                            </div>
                        ) : status.hasQR ? (
                            <div>
                                <p style={{ marginBottom: '15px', fontSize: '1rem' }}>
                                    📱 Escaneie o QR Code com seu WhatsApp:
                                </p>
                                <div style={{
                                    backgroundColor: 'white',
                                    padding: '20px',
                                    borderRadius: '8px',
                                    display: 'inline-block',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                }}>
                                    <img src={status.qrCode} alt="QR Code" style={{ width: '300px', height: '300px' }} />
                                </div>
                                <p style={{ marginTop: '15px', color: '#666' }}>
                                    <i className="fas fa-info-circle"></i> Abra o WhatsApp no celular → Dispositivos Conectados → Conectar Dispositivo
                                </p>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{
                                        width: '12px',
                                        height: '12px',
                                        borderRadius: '50%',
                                        backgroundColor: '#ff3b3c'
                                    }}></div>
                                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#ff3b3c' }}>
                                        ❌ WhatsApp Desconectado
                                    </span>
                                </div>
                                <p style={{ color: '#666' }}>
                                    Aguarde... O QR Code será exibido em alguns segundos.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs - Só aparecem quando conectado */}
                {status.isReady && (
                    <>
                        <div className="tabs-container" style={{ marginBottom: '20px' }}>
                            <button
                                className={`tab-button ${activeTab === 'groups' ? 'active' : ''}`}
                                onClick={() => setActiveTab('groups')}
                                style={{
                                    padding: '12px 24px',
                                    border: 'none',
                                    backgroundColor: activeTab === 'groups' ? 'var(--primary-color)' : '#f0f0f0',
                                    color: activeTab === 'groups' ? 'var(--text-on-yellow)' : '#666',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    borderRadius: '8px 8px 0 0',
                                    marginRight: '5px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <i className="fas fa-users"></i> Grupos
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'send' ? 'active' : ''}`}
                                onClick={() => setActiveTab('send')}
                                style={{
                                    padding: '12px 24px',
                                    border: 'none',
                                    backgroundColor: activeTab === 'send' ? 'var(--primary-color)' : '#f0f0f0',
                                    color: activeTab === 'send' ? 'var(--text-on-yellow)' : '#666',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    borderRadius: '8px 8px 0 0',
                                    marginRight: '5px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <i className="fas fa-paper-plane"></i> Enviar Ofertas
                            </button>
                            <button
                                className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
                                onClick={() => setActiveTab('history')}
                                style={{
                                    padding: '12px 24px',
                                    border: 'none',
                                    backgroundColor: activeTab === 'history' ? 'var(--primary-color)' : '#f0f0f0',
                                    color: activeTab === 'history' ? 'var(--text-on-yellow)' : '#666',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    borderRadius: '8px 8px 0 0',
                                    transition: 'all 0.2s'
                                }}
                            >
                                <i className="fas fa-history"></i> Histórico
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="card-container">
                            {activeTab === 'groups' && <WhatsAppGroups />}
                            {activeTab === 'send' && <SendOffers />}
                            {activeTab === 'history' && <WhatsAppHistory />}
                        </div>
                    </>
                )}
            </div>

            <style>{`
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }
            `}</style>
        </div>
    );
};

export default WhatsAppManager;
